import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Card from 'antd/es/card';
import Typography from 'antd/es/typography';
import Collapse from 'antd/es/collapse';
import Spin from 'antd/es/spin';
import Button from 'antd/es/button';
import Alert from 'antd/es/alert';
import Divider from 'antd/es/divider';
import Progress from 'antd/es/progress';
import Row from 'antd/es/row';
import Col from 'antd/es/col';
import Statistic from 'antd/es/statistic';
import Space from 'antd/es/space';
import Tag from 'antd/es/tag';
import { Radar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
} from 'chart.js';
import api from '../config/api';
import axios from 'axios';
import { logErrorToServer } from '../utils/errorLogger';
import type { TestResult, DimensionScore } from '../types';
import questions from '../data/questions';

const { Title, Text, Paragraph } = Typography;

ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

const Result: React.FC = () => {
  const { testId } = useParams<{ testId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<TestResult | null>(null);
  const [dimensionAnalysis, setDimensionAnalysis] = useState<DimensionScore[]>([]);
  const [maxScores, setMaxScores] = useState<{ [key: string]: number }>({});

  // 计算指定维度的总分
  const calculateDimensionTotalScore = (dimension: string): number => {
    if (!result) return 0;
    
    const dimensionMap: { [key: string]: string } = {
      '躯体化': 'somatization',
      '强迫症状': 'obsessive',
      '人际关系敏感': 'interpersonal',
      '抑郁': 'depression',
      '焦虑': 'anxiety',
      '敌对': 'hostility',
      '恐怖': 'phobic',
      '偏执': 'paranoid',
      '精神病性': 'psychotic'
    };
    
    const englishDimension = dimensionMap[dimension];
    if (!englishDimension) return 0;
    
    const dimensionQuestions = result.answers.filter(
      (answer: any) => {
        const questionDimension = getQuestionDimension(answer.questionId);
        return questionDimension === englishDimension;
      }
    );
    
    return dimensionQuestions.reduce((sum: number, answer: any) => sum + answer.score, 0);
  };

  // 获取指定维度的题目数量
  const getDimensionQuestionCount = (dimension: string): number => {
    const dimensionMap: { [key: string]: string } = {
      '躯体化': 'somatization',
      '强迫症状': 'obsessive',
      '人际关系敏感': 'interpersonal',
      '抑郁': 'depression',
      '焦虑': 'anxiety',
      '敌对': 'hostility',
      '恐怖': 'phobic',
      '偏执': 'paranoid',
      '精神病性': 'psychotic'
    };
    
    const englishDimension = dimensionMap[dimension];
    if (!englishDimension) return 0;
    
    // 使用接口返回的 questions 数据
    if (!result || !result.questions) return 0;
    return result.questions.filter((q: any) => q.dimension === englishDimension).length;
  };

  // 根据问题ID确定维度
  const getQuestionDimension = (id: number) => {
    // 使用接口返回的 questions 数据
    if (!result || !result.questions) return undefined;
    const question = result.questions.find((q: any) => q.id === id);
    return question ? question.dimension : undefined;
  };

  const getSeverityLevel = (score: number): string => {
    if (score < 1.5) return '正常';
    if (score < 2.5) return '轻度';
    if (score < 3.5) return '中度';
    return '重度';
  };

  const getScoreColor = (score: number): string => {
    if (score < 1.5) return '#52c41a';
    if (score < 2.5) return '#faad14';
    if (score < 3.5) return '#fa8c16';
    return '#f5222d';
  };

  const getTotalScoreLevel = (score: number): string => {
    if (score < 160) return '正常';
    if (score < 225) return '轻度';
    if (score < 315) return '中度';
    return '重度';
  };

  const getPositiveItemsLevel = (count: number): string => {
    if (count <= 43) return '正常';
    return '阳性';
  };

  const getPositiveSymptomLevel = (score: number): string => {
    if (score < 1.5) return '正常';
    if (score < 2.5) return '轻度';
    if (score < 3.5) return '中度';
    return '重度';
  };

  const getDimensionDescription = (dimension: string, score: number): string => {
    const level = getSeverityLevel(score);
    const descriptions: { [key: string]: { [key: string]: string } } = {
      "躯体化": {
        "正常": "无明显躯体不适症状",
        "轻度": "近期偶尔感到身体不适（如头痛、胃痛），可能与压力或疲劳有关",
        "中度": "频繁出现多种身体不适，但医学检查未发现明确病因，可能与情绪压力相关",
        "重度": "长期存在多种躯体疼痛或不适，严重影响生活，需排除器质性疾病"
      },
      "强迫症状": {
        "正常": "无明显强迫思维或行为",
        "轻度": "偶尔出现重复想法或行为，但能通过自我调节缓解，不影响日常生活",
        "中度": "反复出现强迫思维或行为，消耗较多时间，可能伴随焦虑",
        "重度": "强迫症状占据大量时间，导致强烈痛苦或功能受损"
      },
      "人际关系敏感": {
        "正常": "人际关系良好，无明显社交困扰",
        "轻度": "偶尔在社交中感到不自在，但对人际关系影响较小",
        "中度": "对他人评价敏感，容易感到被误解或排斥，可能回避社交",
        "重度": "长期存在社交恐惧或自卑，严重影响人际互动"
      },
      "抑郁": {
        "正常": "情绪稳定，无明显抑郁症状",
        "轻度": "情绪偶尔低落，兴趣减退，但能自我调节",
        "中度": "持续情绪抑郁、睡眠或食欲改变，伴随无力感",
        "重度": "长期绝望感、自杀意念或生理功能紊乱"
      },
      "焦虑": {
        "正常": "无明显焦虑症状",
        "轻度": "短暂紧张或担忧，可通过放松技巧缓解",
        "中度": "频繁心悸、出汗等躯体焦虑症状，影响专注力",
        "重度": "持续惊恐发作或广泛性焦虑，无法正常生活"
      },
      "敌对": {
        "正常": "情绪稳定，无明显敌对情绪",
        "轻度": "偶尔易怒，但能控制情绪反应",
        "中度": "经常因小事愤怒，人际关系紧张",
        "重度": "难以控制的攻击冲动或破坏行为"
      },
      "恐怖": {
        "正常": "无明显恐惧症状",
        "轻度": "对特定场景（如高处）轻微回避，不影响生活",
        "中度": "对某些情境（如人群）产生明显恐惧，可能伴随回避行为",
        "重度": "广泛恐惧导致生活受限"
      },
      "偏执": {
        "正常": "无明显偏执倾向",
        "轻度": "偶尔多疑，但能理性分析",
        "中度": "常怀疑他人动机，影响信任关系",
        "重度": "坚信被迫害或阴谋论，可能伴随妄想"
      },
      "精神病性": {
        "正常": "无明显精神病性症状",
        "轻度": "短暂感到孤独或与现实疏离，压力缓解后消失",
        "中度": "偶尔出现幻觉或思维混乱",
        "重度": "频繁脱离现实感或幻觉妄想"
      }
    };

    return descriptions[dimension]?.[level] || "暂无描述";
  };

  const getDimensionAdvice = (dimension: string, score: number): string => {
    const level = getSeverityLevel(score);
    const advice: { [key: string]: { [key: string]: string } } = {
      "躯体化": {
        "正常": "继续保持健康的生活方式，适量运动和规律作息。",
        "轻度": "建议调整作息，观察身体反应，适当进行放松训练。",
        "中度": "建议结合心理放松训练，必要时进行医学检查排除器质性疾病。",
        "重度": "需排除器质性疾病后考虑心身医学干预，建议及时就医。"
      },
      "强迫症状": {
        "正常": "保持积极的生活态度，培养灵活的思维方式。",
        "轻度": "学习放松技巧，培养兴趣爱好转移注意力。",
        "中度": "建议学习认知行为疗法（CBT）技巧，必要时寻求心理咨询。",
        "重度": "需专业心理治疗，建议及时就医。"
      },
      "人际关系敏感": {
        "正常": "继续保持开放和自信的社交态度。",
        "轻度": "可通过社交练习改善，培养自信心。",
        "中度": "建议进行自信训练，学习社交技巧。",
        "重度": "需系统性心理治疗，重建社交信心。"
      },
      "抑郁": {
        "正常": "保持积极乐观的生活态度，培养兴趣爱好。",
        "轻度": "建议增加运动和社会支持，保持规律作息。",
        "中度": "建议心理咨询或评估抑郁风险，必要时寻求专业帮助。",
        "重度": "需心理评估，建议及时就医。"
      },
      "焦虑": {
        "正常": "继续保持良好的心态，培养健康的生活习惯。",
        "轻度": "学习放松技巧，适当运动减压。",
        "中度": "建议正念训练，必要时寻求专业帮助。",
        "重度": "需专业治疗，建议及时就医。"
      },
      "敌对": {
        "正常": "保持情绪稳定，培养健康的情绪表达方式。",
        "轻度": "学习情绪管理技巧，培养同理心。",
        "中度": "建议学习情绪管理技巧，改善人际关系。",
        "重度": "需行为矫正治疗，建议及时就医。"
      },
      "恐怖": {
        "正常": "保持正常的生活节奏，培养自信心。",
        "轻度": "可通过渐进式暴露训练改善，学习放松技巧。",
        "中度": "建议进行系统脱敏训练，必要时寻求专业帮助。",
        "重度": "需暴露疗法等专业干预，建议及时就医。"
      },
      "偏执": {
        "正常": "保持开放和信任的态度，培养理性思维。",
        "轻度": "学习客观分析问题的方法，培养信任感。",
        "中度": "建议心理咨询，改善人际关系。",
        "重度": "需精神科评估，建议及时就医。"
      },
      "精神病性": {
        "正常": "保持健康的生活方式，培养兴趣爱好。",
        "轻度": "学习压力管理技巧，保持规律作息。",
        "中度": "需密切观察，必要时寻求专业帮助。",
        "重度": "需精神科评估，建议及时就医。"
      }
    };

    return advice[dimension]?.[level] || "暂无建议";
  };

  useEffect(() => {
    const fetchResult = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/api/tests/scl-90/${testId}`);
        if (!response.data || !response.data.isCompleted) {
          // 这个接口在链接不存在时会自动补建记录并返回 200，
          // 所以「结果页打不开」表现为 isCompleted 为 false，而不是 404，
          // 之前写在 catch 里的跳转因此从来没生效过。
          // 未完成的测试一律送去答题页，不要让用户停在空白结果页上。
          if (response.data?.isLegacyRecovery) {
            await logErrorToServer(testId!, 'TEST_NOT_FOUND', '旧链接结果已丢失，转至答题页', { page: 'result' });
          }
          navigate(`/test/scl-90/${testId}`, { replace: true });
          return;
        }
        setResult(response.data);
        
        // 处理维度分析
        const dimensionMap: { [key: string]: string } = {
          'somatization': '躯体化',
          'obsessive': '强迫症状',
          'interpersonal': '人际关系敏感',
          'depression': '抑郁',
          'anxiety': '焦虑',
          'hostility': '敌对',
          'phobic': '恐怖',
          'paranoid': '偏执',
          'psychotic': '精神病性'
        };
        
        const analysis = Object.entries(response.data.scores)
          .filter(([key]) => key !== 'other') // 过滤掉 other 维度
          .map(([key, score]) => {
            const chineseDimension = dimensionMap[key] || key;
            return {
              dimension: chineseDimension,
              score: score as number,
              level: getSeverityLevel(score as number),
              description: getDimensionDescription(chineseDimension, score as number),
              advice: getDimensionAdvice(chineseDimension, score as number)
            };
          });
        // console.log(analysis)
        setDimensionAnalysis(analysis);

        // 计算最高分
        const maxScoresObj: { [key: string]: number } = {};
        // console.log('服务器返回的答案数据:', response.data.answers);
        
        // 根据问题ID确定维度（在 useEffect 内部使用 response.data.questions）
        const getQuestionDimensionInEffect = (id: number) => {
          const question = response.data.questions.find((q: any) => q.id === id);
          return question ? question.dimension : undefined;
        };

        Object.entries(dimensionMap).forEach(([key, dimension]) => {
          // console.log(`正在处理维度: ${dimension} (${key})`);
          const dimensionQuestions = response.data.answers.filter(
            (answer: any) => {
              const questionDimension = getQuestionDimensionInEffect(answer.questionId);
              // console.log(`问题 ${answer.questionId} 属于维度:`, questionDimension);
              return questionDimension === key;
            }
          );
          // console.log(`维度 ${dimension} (${key}) 的所有答案:`, dimensionQuestions);
          
          if (dimensionQuestions.length > 0) {
            const scores = dimensionQuestions.map((q: any) => q.score);
            // console.log(`维度 ${dimension} 的所有分数:`, scores);
            const maxScore = Math.max(...scores);
            // console.log(`维度 ${dimension} 的最高分:`, maxScore);
            maxScoresObj[dimension] = maxScore;
          } else {
            // console.log(`维度 ${dimension} 没有答案数据`);
            maxScoresObj[dimension] = 0;
          }
        });
        
        console.log('最终的最高分对象:', maxScoresObj);
        setMaxScores(maxScoresObj);

        setError(null);
      } catch (err) {
        console.error('获取测试结果失败:', err);
        let errorType = 'OTHER';
        let errorMessage = '获取测试结果失败';

        if (axios.isAxiosError(err)) {
          errorMessage = err.response?.data?.message || '获取测试结果失败';
          // 链接失效：用户拿着已购买的旧链接回来查结果，但记录不存在
          if (err.response?.status === 404) {
            errorType = 'TEST_NOT_FOUND';
          }
        }

        setError(errorMessage);
        await logErrorToServer(testId!, errorType, errorMessage, { page: 'result' });

        // 补建开关关闭后，不存在的链接会真的返回 404，这时同样送去答题页，
        // 由答题页统一提示。开关开启时走的是上面 isCompleted 的分支。
        if (errorType === 'TEST_NOT_FOUND') {
          navigate(`/test/scl-90/${testId}`, { replace: true });
        }
      } finally {
        setLoading(false);
      }
    };

    if (testId) {
      fetchResult();
    }
  }, [testId, navigate]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '48px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error || !result) {
    return (
      <div style={{ padding: '24px' }}>
        <Alert
          message="错误"
          description={error || '未找到测试结果'}
          type="error"
          showIcon
        />
      </div>
    );
  }

  const totalScore = result.totalScore;
  const positiveItems = result.positiveItems;
  const positiveSymptomTotal = result.positiveSymptomTotal;

  return (
    <div className="max-w-4xl mx-auto p-4">
      <Card>
        <Title level={2} className="text-center mb-4">SCL-90 症状自评量表测试结果</Title>
        <Text type="secondary" className="text-center block mb-3">注：根据测试链接，随时可找回报告</Text>
            {/* 总评分结果报告表格 */}
 
        <div style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '12px' }}>总评分结果报告</div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead>
            <tr style={{ background: '#f5f5f5' }}>
              <th style={{ paddingLeft: '0px', paddingRight: '0px', paddingTop: '8px', paddingBottom: '8px', border: '1px solid #eee' }}>总评分</th>
              <th style={{ paddingLeft: '0px', paddingRight: '0px', paddingTop: '8px', paddingBottom: '8px', border: '1px solid #eee' }}>平均分</th>
              <th style={{ paddingLeft: '0px', paddingRight: '0px', paddingTop: '8px', paddingBottom: '8px', border: '1px solid #eee' }}>阳性项目数</th>
              <th style={{ paddingLeft: '0px', paddingRight: '0px', paddingTop: '8px', paddingBottom: '8px', border: '1px solid #eee' }}>数值范围</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ paddingLeft: '0px', paddingRight: '0px', paddingTop: '8px', paddingBottom: '8px', border: '1px solid #eee', textAlign: 'center', fontWeight: 'bold' }}>{totalScore}</td>
              <td style={{ paddingLeft: '0px', paddingRight: '0px', paddingTop: '8px', paddingBottom: '8px', border: '1px solid #eee', textAlign: 'center' }}>{(totalScore / 90).toFixed(2)}</td>
              <td style={{ paddingLeft: '0px', paddingRight: '0px', paddingTop: '8px', paddingBottom: '8px', border: '1px solid #eee', textAlign: 'center' }}>{positiveItems}</td>
              <td style={{ paddingLeft: '0px', paddingRight: '0px', paddingTop: '8px', paddingBottom: '8px', border: '1px solid #eee', textAlign: 'center' }}>
                <span style={{
                  color: (totalScore > 160 || positiveItems > 43) ? '#f5222d' : '#52c41a',
                  fontWeight: 'bold'
                }}>{(totalScore > 160 || positiveItems > 43) ? '阳性' : '正常'}</span>
              </td>
            </tr>
          </tbody>
        </table>

        <div style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '12px', marginTop: '12px' }}>各因子得分表</div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead>
            <tr style={{ background: '#f5f5f5' }}>
              <th style={{ paddingLeft: '0px', paddingRight: '0px', paddingTop: '8px', paddingBottom: '8px', border: '1px solid #eee' }}>因子</th>
              <th style={{ paddingLeft: '0px', paddingRight: '0px', paddingTop: '8px', paddingBottom: '8px', border: '1px solid #eee' }}>总分</th>
              <th style={{ paddingLeft: '0px', paddingRight: '0px', paddingTop: '8px', paddingBottom: '8px', border: '1px solid #eee' }}>平均分</th>
              <th style={{ paddingLeft: '0px', paddingRight: '0px', paddingTop: '8px', paddingBottom: '8px', border: '1px solid #eee' }}>参考结果</th>
            </tr>
          </thead>
          <tbody>
            {dimensionAnalysis.map(({ dimension, score, level }) => {
              const totalScore = calculateDimensionTotalScore(dimension);
              const questionCount = getDimensionQuestionCount(dimension);
              const averageScore = questionCount > 0 ? (totalScore / questionCount) : 0;
              
              return (
                <tr key={dimension}>
                  <td style={{ paddingLeft: '0px', paddingRight: '0px', paddingTop: '8px', paddingBottom: '8px', border: '1px solid #eee', textAlign: 'center' }}>{dimension}</td>
                  <td style={{ paddingLeft: '0px', paddingRight: '0px', paddingTop: '8px', paddingBottom: '8px', border: '1px solid #eee', textAlign: 'center', fontWeight: 'bold' }}>{totalScore}</td>
                  <td style={{ paddingLeft: '0px', paddingRight: '0px', paddingTop: '8px', paddingBottom: '8px', border: '1px solid #eee', textAlign: 'center', fontWeight: 'bold' }}>{averageScore.toFixed(2)}</td>
                  <td style={{ paddingLeft: '0px', paddingRight: '0px', paddingTop: '8px', paddingBottom: '8px', border: '1px solid #eee', textAlign: 'center' }}>
                    <span style={{
                      color: score < 1.5 ? '#52c41a' : 
                             score < 2.5 ? '#faad14' : 
                             score < 3.5 ? '#fa8c16' : '#f5222d',
                      fontWeight: 'bold'
                    }}>{level}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* 说明文字 */}
        <div style={{ 
          marginTop: '16px', 
          padding: '12px', 
          backgroundColor: '#f6f8fa', 
          borderRadius: '6px',
          fontSize: '13px',
          lineHeight: '1.6',
          color: '#333',
          textAlign: 'left'
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>说明：</div>
          <div>
            按全国正常人SCL-90常模(N=1388)(1-5级评分)（国内医院常用常模），当测试总分超过160分，或阳性项目数超过43项，需考虑筛选阳性，阳性即说明可能存在问题。
          </div>
          <div style={{ marginTop: '8px' }}>
            您的测试总分为<span style={{ fontWeight: 'bold' }}>{totalScore}</span>分，您的阳性项目数为<span style={{ fontWeight: 'bold' }}>{positiveItems}</span>项，综合以上结果，您的总的评估结果为<span style={{ fontWeight: 'bold', color: (totalScore > 160 || positiveItems > 43) ? '#f5222d' : '#52c41a' }}>{(totalScore > 160 || positiveItems > 43) ? '阳性' : '正常'}</span>，按总症状指数，总的测试评级为<span style={{ fontWeight: 'bold' }}>{getTotalScoreLevel(totalScore)}</span>。
          </div>
          <div style={{ marginTop: '8px', fontStyle: 'italic', color: '#666' }}>
            由于量表只是辅助筛查工具，因此量表测试结果仅供参考。
          </div>
        </div>


        {/* 总体评估 */}
        <div className="mb-8 mt-8">
          <Title level={3}>详细数据</Title>
          <Row gutter={[16, 16]}>
            {/* 总分展示 */}
            <Col span={24}>
              <Card className="bg-gray-50">
                <div className="flex justify-between items-center mb-4">
                  <Text strong className="text-lg">总分</Text>
                  <div className="flex items-center gap-2">
                    <Text className="text-2xl font-bold">{totalScore}</Text>
                    <Tag color={totalScore < 160 ? 'success' : totalScore < 225 ? 'warning' : totalScore < 315 ? 'error' : 'error'}>
                      {getTotalScoreLevel(totalScore)}
                    </Tag>
                  </div>
                </div>
                <div className="relative">
                  <Progress 
                    percent={(totalScore / 450) * 100}
                    strokeColor={{
                      '0%': '#52c41a',
                      '35.5%': '#52c41a',
                      '35.6%': '#ffd54f',
                      '50%': '#ffd54f',
                      '50.1%': '#fa8c16',
                      '70%': '#fa8c16',
                      '70.1%': '#f5222d'
                    }}
                    format={() => `${totalScore}/450`}
                    strokeWidth={12}
                    className="mb-2"
                  />
                  <div className="flex text-xs text-gray-500" style={{ position: 'relative', marginTop: '4px', width: 'calc(100% - 55px)', marginLeft: '3px', marginBottom: '24px' }}>
                    <span style={{ position: 'absolute', left: '0', transform: 'translateX(-50%)' }}>0</span>
                    <span style={{ position: 'absolute', left: '35.5%', transform: 'translateX(-50%)' }}>160</span>
                    <span style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}>225</span>
                    <span style={{ position: 'absolute', left: '70%', transform: 'translateX(-50%)' }}>315</span>
                    <span style={{ position: 'absolute', left: '100%', transform: 'translateX(-50%)' }}>450</span>
                  </div>
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>正常 (&lt;160)</span>
                    <span>轻度 (160-224)</span>
                    <span>中度 (225-314)</span>
                    <span>重度 (≥315)</span>
                  </div>
                </div>
                <Paragraph className="text-sm mt-4">
                  <Text strong>解读：</Text> 总分反映整体心理健康状况的严重程度。
                  {totalScore < 160 ? (
                    '您的总分在正常范围内，表明整体心理健康状况良好。'
                  ) : totalScore < 225 ? (
                    '您的总分处于轻度范围，心理不适在正常压力反应范围内，可能存在短期情绪波动或生活事件影响，建议通过自我调节（如运动、社交）缓解。'
                  ) : totalScore < 315 ? (
                    '您的总分处于中度范围，整体心理痛苦较明显，可能存在多个维度的症状（如焦虑、抑郁），建议心理咨询或进一步评估。'
                  ) : (
                    '您的总分处于重度范围，心理症状广泛且严重，可能符合多种心理障碍诊断标准（如抑郁症、焦虑症），建议尽快寻求精神科或心理治疗干预。'
                  )}
                </Paragraph>
              </Card>
            </Col>

            {/* 阳性项目数和阳性症状均分 */}
            <Col span={24}>
              <Space direction="vertical" size="middle" className="w-full">
                {/* 阳性项目数 */}
                <Card className="bg-gray-50">
                  <div className="flex justify-between items-center mb-4">
                    <Text strong className="text-lg">阳性项目数</Text>
                    <div className="flex items-center gap-2">
                      <Text className="text-2xl font-bold">{positiveItems}</Text>
                      <Tag color={positiveItems <= 43 ? 'success' : 'error'}>
                        {getPositiveItemsLevel(positiveItems)}
                      </Tag>
                    </div>
                  </div>
                  <div className="flex justify-center">
                    <Progress 
                      type="circle"
                      percent={(positiveItems / 90) * 100}
                      strokeColor={{
                        '0%': '#52c41a',
                        '47.8%': '#52c41a',
                        '47.9%': '#f5222d'
                      }}
                      format={() => `${positiveItems}/90`}
                      width={120}
                      strokeWidth={8}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-gray-500 mt-4">
                    <span>正常 (≤43)</span>
                    <span>阳性 (&gt;43)</span>
                  </div>
                  <Paragraph className="text-sm mt-4">
                    <Text strong>解读：</Text> 阳性项目数反映症状的广泛性（≥2分视为阳性项目）。
                    {positiveItems <= 43 ? (
                      '您的阳性项目数在正常范围内，表明症状较少。'
                    ) : (
                      '您的阳性项目数处于阳性范围，症状分布较广，涉及情绪、躯体化等多方面，提示整体心理状态失衡，建议综合干预（如心理治疗+生活方式调整）。'
                    )}
                  </Paragraph>
                </Card>

                {/* 阳性症状均分 */}
                <Card className="bg-gray-50">
                  <div className="flex justify-between items-center mb-4">
                    <Text strong className="text-lg">阳性症状均分</Text>
                    <div className="flex items-center gap-2">
                      <Text className="text-2xl font-bold">{positiveSymptomTotal.toFixed(1)}</Text>
                      <Tag color={positiveSymptomTotal < 1.5 ? 'success' : positiveSymptomTotal < 2.5 ? 'warning' : positiveSymptomTotal < 3.5 ? 'error' : 'error'}>
                        {getPositiveSymptomLevel(positiveSymptomTotal)}
                      </Tag>
                    </div>
                  </div>
                  <div className="relative">
                    <Progress 
                      percent={(positiveSymptomTotal / 5) * 100}
                      strokeColor={{
                        '0%': '#52c41a',
                        '30%': '#52c41a',
                        '30.1%': '#ffd54f',
                        '50%': '#ffd54f',
                        '50.1%': '#fa8c16',
                        '70%': '#fa8c16',
                        '70.1%': '#f5222d'
                      }}
                      format={() => `${positiveSymptomTotal.toFixed(1)}/5`}
                      strokeWidth={12}
                      className="mb-2"
                    />
                    <div className="flex text-xs text-gray-500" style={{ position: 'relative', marginTop: '4px', width: 'calc(100% - 55px)', marginLeft: '3px', marginBottom: '24px' }}>
                      <span style={{ position: 'absolute', left: '0', transform: 'translateX(-50%)' }}>0</span>
                      <span style={{ position: 'absolute', left: '30%', transform: 'translateX(-50%)' }}>1.5</span>
                      <span style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}>2.5</span>
                      <span style={{ position: 'absolute', left: '70%', transform: 'translateX(-50%)' }}>3.5</span>
                      <span style={{ position: 'absolute', left: '100%', transform: 'translateX(-50%)' }}>5.0</span>
                    </div>
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>正常 (&lt;1.5)</span>
                      <span>轻度 (1.5-2.4)</span>
                      <span>中度 (2.5-3.4)</span>
                      <span>重度 (≥3.5)</span>
                    </div>
                  </div>
                  <Paragraph className="text-sm mt-4">
                    <Text strong>解读：</Text> 阳性症状均分反映阳性项目的平均严重程度（计算公式：阳性项目总分/阳性项目数）。
                    {positiveSymptomTotal < 1.5 ? (
                      '您的阳性症状均分在正常范围内，表明症状较轻。'
                    ) : positiveSymptomTotal < 2.5 ? (
                      '您的阳性症状均分处于轻度范围，阳性症状较轻，多为偶发或可自我控制，建议观察症状变化规律（如是否与压力事件相关）。'
                    ) : positiveSymptomTotal < 3.5 ? (
                      '您的阳性症状均分处于中度范围，症状强度中等，已对生活产生一定影响（如工作效率下降、人际关系紧张），建议学习应对技巧（如CBT、放松训练）。'
                    ) : (
                      '您的阳性症状均分处于重度范围，症状强烈且难以耐受，可能伴随躯体化反应（如失眠、疼痛）或情绪崩溃，建议咨询心理医生。'
                    )}
                  </Paragraph>
                </Card>
              </Space>
            </Col>
          </Row>
        </div>

        <Divider />

        {/* 各维度评估 */}
        <div className="mb-8">
          <Title level={3}>各维度评估</Title>
          <Space direction="vertical" size="large" className="w-full">
  
            {/* <Card className="bg-gray-50">
              <div style={{ height: '300px' }}>
                <Radar
                  data={{
                    labels: [
                      '躯体化',
                      '强迫症状',
                      '人际关系敏感',
                      '抑郁',
                      '焦虑',
                      '敌对',
                      '恐怖',
                      '偏执',
                      '精神病性'
                    ],
                    datasets: [
                      {
                        label: '平均分',
                        data: [
                          dimensionAnalysis.find(d => d.dimension === '躯体化')?.score || 0,
                          dimensionAnalysis.find(d => d.dimension === '强迫症状')?.score || 0,
                          dimensionAnalysis.find(d => d.dimension === '人际关系敏感')?.score || 0,
                          dimensionAnalysis.find(d => d.dimension === '抑郁')?.score || 0,
                          dimensionAnalysis.find(d => d.dimension === '焦虑')?.score || 0,
                          dimensionAnalysis.find(d => d.dimension === '敌对')?.score || 0,
                          dimensionAnalysis.find(d => d.dimension === '恐怖')?.score || 0,
                          dimensionAnalysis.find(d => d.dimension === '偏执')?.score || 0,
                          dimensionAnalysis.find(d => d.dimension === '精神病性')?.score || 0
                        ],
                        backgroundColor: 'rgba(136, 132, 216, 0.2)',
                        borderColor: 'rgba(136, 132, 216, 1)',
                        borderWidth: 2,
                        pointBackgroundColor: 'rgba(136, 132, 216, 1)',
                        pointBorderColor: '#fff',
                        pointHoverBackgroundColor: '#fff',
                        pointHoverBorderColor: 'rgba(136, 132, 216, 1)',
                        pointRadius: 5,
                        pointHoverRadius: 7
                      },
                      {
                        label: '最高分',
                        data: [
                          maxScores['躯体化'] || 0,
                          maxScores['强迫症状'] || 0,
                          maxScores['人际关系敏感'] || 0,
                          maxScores['抑郁'] || 0,
                          maxScores['焦虑'] || 0,
                          maxScores['敌对'] || 0,
                          maxScores['恐怖'] || 0,
                          maxScores['偏执'] || 0,
                          maxScores['精神病性'] || 0
                        ],
                        backgroundColor: 'rgba(255, 99, 132, 0.1)',
                        borderColor: 'rgba(255, 99, 132, 1)',
                        borderWidth: 2,
                        pointBackgroundColor: 'rgba(255, 99, 132, 1)',
                        pointBorderColor: '#fff',
                        pointHoverBackgroundColor: '#fff',
                        pointHoverBorderColor: 'rgba(255, 99, 132, 1)',
                        pointRadius: 5,
                        pointHoverRadius: 7,
                        fill: false
                      }
                    ]
                  }}
                  options={{
                    maintainAspectRatio: false,
                    scales: {
                      r: {
                        beginAtZero: true,
                        max: 5,
                        ticks: {
                          stepSize: 1,
                          font: {
                            size: 10
                          }
                        },
                        grid: {
                          color: 'rgba(0, 0, 0, 0.1)'
                        },
                        angleLines: {
                          color: 'rgba(0, 0, 0, 0.1)'
                        },
                        pointLabels: {
                          font: {
                            size: 11
                          },
                          padding: 15
                        }
                      }
                    },
                    plugins: {
                      legend: {
                        position: 'bottom',
                        labels: {
                          usePointStyle: true,
                          pointStyle: 'circle',
                          padding: 15,
                          font: {
                            size: 10
                          }
                        }
                      },
                      tooltip: {
                        callbacks: {
                          label: (context) => {
                            return `${context.dataset.label}: ${context.raw}`;
                          }
                        }
                      }
                    },
                    elements: {
                      line: {
                        tension: 0.1
                      }
                    },
                    layout: {
                      padding: {
                        top: 10,
                        bottom: 10
                      }
                    }
                  }}
                />
              </div>
            </Card> */}
 


            {/* 各因子详细分析 */}
            {dimensionAnalysis.map(({ dimension, score, level, description, advice }) => (
              <Card 
                key={dimension} 
                style={{ 
                  marginBottom: '12px', 
                  borderRadius: '12px',
                  border: 'none',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}
              >
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ 
                    fontSize: '16px', 
                    fontWeight: 'bold', 
                    marginBottom: '8px'
                  }}>
                    {dimension}因子分析
                  </div>
                  
                  <div style={{ 
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '12px',
                    marginBottom: '12px',
                    fontSize: '14px',
                    color: '#1890ff',
                    fontWeight: 'bold'
                  }}>
                    {/* <span>题目数: {getDimensionQuestionCount(dimension)}</span> */}
                    <span>测评评分: {(() => {
                      const questionCount = getDimensionQuestionCount(dimension);
                      const totalScore = calculateDimensionTotalScore(dimension);
                      return questionCount > 0 ? (totalScore / questionCount).toFixed(2) : '0.00';
                    })()}</span>
                    <span>总分: {calculateDimensionTotalScore(dimension)}</span>
                  </div>
                  
                  {/* 严重程度指示条 */}
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      fontSize: '10px',
                      color: '#666',
                      marginBottom: '4px'
                    }}>
                      <span>正常</span>
                      <span>轻度</span>
                      <span>中度</span>
                      <span>重度</span>
                    </div>
                    <div style={{
                      height: '8px',
                      backgroundColor: '#f0f0f0',
                      borderRadius: '4px',
                      position: 'relative',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        position: 'absolute',
                        left: score < 1.5 ? '0%' : score < 2.5 ? '25%' : score < 3.5 ? '50%' : '75%',
                        width: '25%',
                        height: '100%',
                        backgroundColor: getScoreColor(score),
                        borderRadius: '4px'
                      }} />
                    </div>
                  </div>

                  {/* 详细解释 */}
                  <div style={{ 
                    fontSize: '12px', 
                    color: '#666',
                    lineHeight: '1.5',
                    marginBottom: '8px'
                  }}>
                    {description}
                  </div>

                  {/* 建议 */}
                  <div style={{ 
                    fontSize: '12px', 
                    color: '#1890ff',
                    lineHeight: '1.5',
                    fontStyle: 'italic'
                  }}>
                    建议：{advice}
                  </div>
                </div>
              </Card>
            ))}
          </Space>
        </div>

        <Divider />

        {/* 关于结果的说明 */}
        <div className="mb-8">
          <Title level={3}>关于结果的说明</Title>
          <Card>
            <Paragraph className="text-sm">
              本报告基于您的自评数据生成，可作为心理状态的参考，但不能替代专业医生的诊断。
            </Paragraph>
            <Paragraph className="text-sm">
              如果某些结果让您感到担忧，建议与心理咨询师或精神科医生进一步沟通。
            </Paragraph>
          </Card>
        </div>
      </Card>
    </div>
  );
};

export default Result;